"use client"

import { memo, useCallback, useMemo, useState } from "react"
import {
  Check,
  ChevronRight,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Smartphone,
} from "lucide-react"

import { cn } from "@shared/ui/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/components/dialog"

import { walletChains } from "../config"
import { useWalletChain } from "../context/wallet-context"
import { featuredWallets, useFeaturedWalletConnect } from "../wallet"
import type { FeaturedWallet } from "../wallet/types"
import { WalletIcon } from "./wallet-icon"

const MOBILE_UA =
  typeof navigator !== "undefined" &&
  /android|iphone|ipad|ipod|harmony/i.test(navigator.userAgent)

type WalletRowProps = {
  wallet: FeaturedWallet
  pendingId: string | null
  detected: boolean
  mobile: boolean
  labels: {
    detected: string
    openApp: string
    install: string
    rejected: string
  }
  onSelect: (wallet: FeaturedWallet) => void
}

const WalletRow = memo(function WalletRow({
  wallet,
  pendingId,
  detected,
  mobile,
  labels,
  onSelect,
}: WalletRowProps) {
  const isPending = pendingId === wallet.id
  const status = detected
    ? {
        icon: <Check className="size-3.5 shrink-0" />,
        text: labels.detected,
        className: "text-emerald-400",
      }
    : mobile
      ? {
          icon: <Smartphone className="size-3.5 shrink-0" />,
          text: labels.openApp,
          className: "text-muted-foreground",
        }
      : {
          icon: <ExternalLink className="size-3.5 shrink-0" />,
          text: labels.install,
          className: "text-muted-foreground",
        }

  return (
    <button
      type="button"
      disabled={Boolean(pendingId)}
      onClick={() => onSelect(wallet)}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition-colors",
        "hover:border-primary/40 hover:bg-white/10 disabled:opacity-60",
        isPending && "border-primary/50",
      )}
    >
      <span
        className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/90 p-1.5 shadow-inner"
        aria-hidden="true"
      >
        {isPending ? (
          <Loader2 className="size-5 animate-spin text-primary" />
        ) : (
          <WalletIcon
            logo={wallet.logo}
            name={wallet.name}
            className="size-full object-contain"
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium leading-tight">
          {wallet.name}
        </span>
        <span
          className={cn(
            "mt-0.5 flex items-center gap-1 text-[11px] leading-none",
            status.className,
          )}
        >
          {status.icon}
          <span className="truncate">{status.text}</span>
        </span>
      </span>
      {isPending ? null : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground/70" />
      )}
    </button>
  )
})

export function ConnectWalletModal() {
  const { modalOpen, modalLabels, closeWalletModal } = useWalletChain()
  const { connectFeatured, walletDetectedMap } = useFeaturedWalletConnect()
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rowLabels = useMemo(
    () => ({
      detected: modalLabels.detected,
      openApp: modalLabels.openApp,
      install: modalLabels.install,
      rejected: modalLabels.rejected,
    }),
    [
      modalLabels.detected,
      modalLabels.install,
      modalLabels.openApp,
      modalLabels.rejected,
    ],
  )

  const resetModalState = useCallback(() => {
    setPendingId(null)
    setError(null)
  }, [])

  const handleSelectWallet = useCallback(
    async (wallet: FeaturedWallet) => {
      setError(null)
      setPendingId(wallet.id)
      try {
        await connectFeatured(wallet)
      } catch (cause) {
        const message =
          cause instanceof Error ? cause.message : modalLabels.rejected
        if (!message.includes("Redirecting")) {
          setError(
            message === "Connection cancelled" ? modalLabels.rejected : message,
          )
        }
      } finally {
        setPendingId(null)
      }
    },
    [connectFeatured, modalLabels.rejected],
  )

  return (
    <Dialog
      open={modalOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetModalState()
          closeWalletModal()
        }
      }}
    >
      <DialogContent
        className={cn(
          "max-w-[24rem] gap-0 border border-white/10 bg-popover p-0 sm:max-w-[24rem]",
          "data-open:animate-none data-closed:animate-none",
        )}
      >
        <div className="flex flex-col gap-4 p-6">
          <DialogHeader className="text-center sm:text-left">
            <p className="mb-2 inline-flex w-fit items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-primary">
              {walletChains.tron.label}
              <span className="mx-1 text-primary/50">·</span>
              {walletChains.tron.symbol}
            </p>
            <DialogTitle>{modalLabels.title}</DialogTitle>
            <DialogDescription>{modalLabels.subtitle}</DialogDescription>
          </DialogHeader>

          <div className="flex max-h-[min(60vh,28rem)] flex-col gap-2 overflow-y-auto pr-0.5">
            {featuredWallets.map((wallet) => (
              <WalletRow
                key={wallet.id}
                wallet={wallet}
                pendingId={pendingId}
                detected={walletDetectedMap[wallet.id] ?? false}
                mobile={MOBILE_UA}
                labels={rowLabels}
                onSelect={(item) => void handleSelectWallet(item)}
              />
            ))}
          </div>

          {error ? (
            <p className="text-center text-xs text-red-400">{error}</p>
          ) : null}

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5 shrink-0 text-emerald-400" />
            {modalLabels.secure}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
